import {type UpmPlantsHierarchyNode} from '@domain/upm'
import {TimeZone} from '@hconnect/uikit'
import {SelectionItem} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/types'
import {FilterTextField} from '@widgets/common-filters'

import {
  AreaFilter,
  AssigneeSearchFilter,
  CreatedBySearchFilter,
  CriticalityFilter,
  DateRangeFilter,
  PlantFilter,
  PlantRequiredTooltip,
  PomActionSourceFilter,
  PomCostLeadershipFilter,
  PomQuestionCategoryFilter,
  PomQuestionFilter,
  PomSectionFilter,
  PomTopicFilter,
  TaskCategoryFilter,
  TaskEquipmentSearchFilter,
  TaskMainEquipmentSearchFilter,
  TaskPriorityFilter,
  TaskProcessStageFilter,
  TaskStatusFilter,
  FilterIncompatibleTooltip
} from '../../components/filters'
import {TasksFilterKey, TasksFilterKeyName} from '../../consts'
import {FilterConfig, TasksFiltersState, TaskSource} from '../../types'
import {isFilterCompatibleWithVisibleFilters} from '../../utils'

const ALL_TASK_SOURCES: TaskSource[] = [
  TaskSource.COCKPIT,
  TaskSource.EASY_POM,
  TaskSource.PROCESS_WORK_ORDER,
  TaskSource.MACHINE_SENTRY_ACTIONS,
  TaskSource.OIL_ANALYSIS_ACTIONS
]

const filterSourcesConfig: Record<TasksFilterKeyName, TaskSource[]> = {
  [TasksFilterKey.TITLE]: ALL_TASK_SOURCES,
  [TasksFilterKey.DUE_DATE_RANGE]: ALL_TASK_SOURCES,
  [TasksFilterKey.CREATED_DATE_RANGE]: ALL_TASK_SOURCES,
  [TasksFilterKey.STATUSES]: ALL_TASK_SOURCES,
  [TasksFilterKey.ASSIGNEE]: [
    TaskSource.COCKPIT,
    TaskSource.EASY_POM,
    TaskSource.PROCESS_WORK_ORDER
  ],
  [TasksFilterKey.CREATED_BY]: [
    TaskSource.COCKPIT,
    TaskSource.EASY_POM,
    TaskSource.PROCESS_WORK_ORDER
  ],
  [TasksFilterKey.CATEGORIES]: [TaskSource.COCKPIT],
  [TasksFilterKey.PRIORITIES]: [TaskSource.COCKPIT],
  [TasksFilterKey.AREA]: ALL_TASK_SOURCES,
  [TasksFilterKey.PLANT]: ALL_TASK_SOURCES,
  [TasksFilterKey.PROCESS_STAGES]: [TaskSource.COCKPIT, TaskSource.PROCESS_WORK_ORDER],
  [TasksFilterKey.EQUIPMENT]: [TaskSource.COCKPIT, TaskSource.PROCESS_WORK_ORDER],
  [TasksFilterKey.MAIN_EQUIPMENT]: [TaskSource.COCKPIT, TaskSource.PROCESS_WORK_ORDER],
  [TasksFilterKey.POM_ACTION_SOURCES]: [TaskSource.EASY_POM],
  [TasksFilterKey.POM_QUESTION_CATEGORIES]: [TaskSource.EASY_POM],
  [TasksFilterKey.POM_COST_LEADERSHIP]: [TaskSource.EASY_POM],
  [TasksFilterKey.POM_SECTIONS]: [TaskSource.EASY_POM],
  [TasksFilterKey.POM_TOPICS]: [TaskSource.EASY_POM],
  [TasksFilterKey.POM_QUESTIONS]: [TaskSource.EASY_POM],
  [TasksFilterKey.OIL_ANALYSIS_CRITICALITY]: [TaskSource.OIL_ANALYSIS_ACTIONS]
}

const plantRequiredFilters: TasksFilterKeyName[] = [
  TasksFilterKey.PROCESS_STAGES,
  TasksFilterKey.EQUIPMENT,
  TasksFilterKey.MAIN_EQUIPMENT,
  TasksFilterKey.OIL_ANALYSIS_CRITICALITY
]

const isPlantRequired = (filterKey: TasksFilterKeyName): boolean => {
  return plantRequiredFilters.includes(filterKey)
}

const getFilterAvailabilityProps = ({
  key,
  visibleFilters,
  label,
  hasPlantSelected
}: Pick<CreateConfigParams, 'key' | 'visibleFilters'> & {
  label: string
  hasPlantSelected?: boolean
}) => {
  const props: {disabled?: boolean; tooltipTitle?: React.ReactNode} = {}

  if (!isFilterCompatibleWithVisibleFilters(key, visibleFilters, filterSourcesConfig)) {
    props.disabled = true
    props.tooltipTitle = <FilterIncompatibleTooltip />
  } else if (isPlantRequired(key) && !hasPlantSelected) {
    props.disabled = true
    props.tooltipTitle = <PlantRequiredTooltip disabledFilter={label} />
  }

  return props
}

export type TasksFiltersStateValue = TasksFiltersState[keyof TasksFiltersState]

export type TaskFilterConfig = FilterConfig<TasksFilterKeyName>

type CreateConfigParams = {
  key: TasksFilterKeyName
  label: string
  filters: TasksFiltersState
  onChange: (key: TasksFilterKeyName, value: TasksFiltersStateValue) => void
  clearFilter: (key: TasksFilterKeyName) => void
  visibleFilters: TasksFilterKeyName[]
}

export const createTextFilter = ({
  key,
  label,
  filters,
  onChange,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <FilterTextField<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters[key] as string}
      onChange={onChange}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createDateRangeFilter = ({
  key,
  label,
  filters,
  onChange,
  timezone,
  selectionItems,
  visibleFilters
}: CreateConfigParams & {
  timezone: TimeZone
  selectionItems: SelectionItem<string>[]
}): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <DateRangeFilter
      timeRangeParam={filters[key] as string}
      onTimeRangeParamChange={(value) => onChange(key, value)}
      timezone={timezone}
      selectionItems={selectionItems}
      label={label}
      dataTestId={`${key}-date-picker-toggle-button`}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createCategoriesFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <TaskCategoryFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.categories}
      onChange={onChange}
      clearFilter={clearFilter}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createStatusesFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <TaskStatusFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.statuses}
      onChange={onChange}
      clearFilter={clearFilter}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createAssigneeFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <AssigneeSearchFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.assignee}
      onChange={onChange}
      clearFilter={clearFilter}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createCreatedByFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <CreatedBySearchFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.createdBy}
      onChange={onChange}
      clearFilter={clearFilter}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createPrioritiesFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <TaskPriorityFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.priorities}
      onChange={onChange}
      clearFilter={clearFilter}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createPomActionSourceFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <PomActionSourceFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.pomActionSources}
      onChange={onChange}
      clearFilter={clearFilter}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createPomCostLeadershipFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <PomCostLeadershipFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.pomCostLeadership}
      onChange={onChange}
      clearFilter={clearFilter}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createPomSectionFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <PomSectionFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.pomSections}
      onChange={onChange}
      clearFilter={clearFilter}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createPomTopicFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <PomTopicFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.pomTopics}
      onChange={onChange}
      clearFilter={clearFilter}
      selectedSectionIds={filters.pomSections}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createPomQuestionFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <PomQuestionFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.pomQuestions}
      onChange={onChange}
      clearFilter={clearFilter}
      selectedSectionIds={filters.pomSections}
      selectedTopicIds={filters.pomTopics}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createPomQuestionCategoryFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <PomQuestionCategoryFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.pomQuestionCategories}
      onChange={onChange}
      clearFilter={clearFilter}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createAreaFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters,
  hierarchyData
}: CreateConfigParams & {hierarchyData?: UpmPlantsHierarchyNode}): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <AreaFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.area}
      onChange={onChange}
      clearFilter={clearFilter}
      hierarchyData={hierarchyData}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createPlantFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters,
  allowedPlantUpmIds
}: CreateConfigParams & {allowedPlantUpmIds?: string[]}): TaskFilterConfig => ({
  value: key,
  label,
  filter: (
    <PlantFilter<TasksFilterKeyName>
      name={key}
      label={label}
      value={filters.plant}
      onChange={onChange}
      clearFilter={clearFilter}
      allowedPlantUpmIds={allowedPlantUpmIds}
    />
  ),
  ...getFilterAvailabilityProps({key, visibleFilters, label})
})

export const createProcessStagesFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => {
  const plantFilterValue = filters.plant

  return {
    value: key,
    label,
    filter: !!plantFilterValue && (
      <TaskProcessStageFilter<TasksFilterKeyName>
        name={key}
        label={label}
        value={filters.processStages}
        onChange={onChange}
        clearFilter={clearFilter}
        plantId={plantFilterValue}
      />
    ),
    ...getFilterAvailabilityProps({
      key,
      visibleFilters,
      label,
      hasPlantSelected: !!plantFilterValue
    })
  }
}

export const createMainEquipmentFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => {
  const plantFilterValue = filters.plant

  return {
    value: key,
    label,
    filter: !!plantFilterValue && (
      <TaskMainEquipmentSearchFilter<TasksFilterKeyName>
        name={key}
        label={label}
        value={filters.mainEquipment}
        onChange={onChange}
        clearFilter={clearFilter}
        plantId={plantFilterValue}
      />
    ),
    ...getFilterAvailabilityProps({
      key,
      visibleFilters,
      label,
      hasPlantSelected: !!plantFilterValue
    })
  }
}

export const createEquipmentFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => {
  const plantFilterValue = filters.plant

  return {
    value: key,
    label,
    filter: !!plantFilterValue && (
      <TaskEquipmentSearchFilter<TasksFilterKeyName>
        name={key}
        label={label}
        value={filters.equipment}
        onChange={onChange}
        clearFilter={clearFilter}
        plantId={plantFilterValue}
      />
    ),
    ...getFilterAvailabilityProps({
      key,
      visibleFilters,
      label,
      hasPlantSelected: !!plantFilterValue
    })
  }
}

export const createCriticalityFilter = ({
  key,
  label,
  filters,
  onChange,
  clearFilter,
  visibleFilters
}: CreateConfigParams): TaskFilterConfig => {
  const plantFilterValue = filters.plant

  return {
    value: key,
    label,
    filter: !!plantFilterValue && (
      <CriticalityFilter<TasksFilterKeyName>
        name={key}
        label={label}
        value={filters.oilAnalysisCriticality}
        onChange={onChange}
        clearFilter={clearFilter}
        plantId={plantFilterValue}
      />
    ),
    ...getFilterAvailabilityProps({
      key,
      visibleFilters,
      label,
      hasPlantSelected: !!plantFilterValue
    })
  }
}
